import React, { useState } from 'react';
import { Modal, Box, ButtonGroup, Button, Link, Paper, TextField, Stack, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import Styles from '../../common/styles/styles';
import { validateUserName, validateEmailAddr } from '../../common/utils/validationUtil';

export default function ForgotPasswordModal() {
  
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const isFormValid = Object.values(errors).filter(value => value !=='').length > 0;

  const title = 'Reset Password';

  const handleOpen = () => {
    setOpen(true);    
  }

  const handleClose = () => {
    setOpen(false);
    setFormData({ username: '', emailaddr: '' });
    setErrors({});
    setIsLoading(false);
  }

  const handleInputChange = (field) => (event) => {
      const value = event.target.value;
      setFormData({ ...formData, [field]: value });
    
      const newErrors = { ...errors };
    
      if (field === 'username') newErrors.username = validateUserName(value);
      if (field === 'emailaddr') newErrors.emailaddr = validateEmailAddr(value);
    
      setErrors(newErrors);
  }

  const [formData, setFormData] = useState({
      username: '',
      emailaddr: ''
  });
  
  const requestPasswordReset = async () => {
      setIsLoading(true);
      try {
          const response = await fetch(`${process.env.REACT_APP_API}/users/request-password-reset`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData),
          });

          const result = await response.json();

          if (result.message === 'success') {
            toast.success('Password reset email sent! Please check your email for further instructions.', { autoClose: 5000 });     
            handleClose();
          } else {
            toast.error(result.message);
          }
      } catch (error) {
        console.error('Password reset request error:', error);
        toast.error('Network error. Please try again.');
      } finally {
        setIsLoading(false);
      }
  }
  
  const handlePasswordResetRequest = (event) => {
    event.preventDefault();
    event.stopPropagation();
    requestPasswordReset();
  };

  const textFieldStyles = Styles.textFieldStyles;

  return (
    <>
        <Link 
          sx={{ 
            ml: 5, 
            outline: 'none',
            '&:focus': {
              outline: 'none'
            }
          }} 
          onClick={handleOpen} 
          style={{ cursor: 'pointer' }} 
          underline="hover" 
          color="#c1d4e7"
        >
          {title}
        </Link>
        <Modal 
          open={open} 
          onClose={handleClose}
          sx={{
            '&:focus': {
              outline: 'none'
            },
            '& .MuiModal-backdrop': {
              outline: 'none'
            }
          }}
          style={{
            overlay: {
              backgroundColor: '#080030', // Semi-transparent black
            },
            content: {
              backgroundColor: '#080030',
            },
          }}
        >
            <Box sx={{color: 'white', p: 4, outline: 'none', '&:focus': { outline: 'none' }}}>
                <Box
                  component={Paper}
                  elevation={3}
                  sx={{
                    mx: 'auto',
                    width: 400,
                    height: 375,
                    bgcolor: '#1e1e1e',
                    p: 2,
                    mt: 20,
                    outline: 'none',
                    '&:focus': {
                      outline: 'none'
                    }
                  }}
                >
                    <Typography variant="h4" component="h2" color="white" mb={2}>
                      {title}
                    </Typography>
                    
                    <Typography variant="body2" color="white" sx={{ mb: 3, opacity: 0.8 }}>
                      Enter your username and email address to receive password reset instructions.
                    </Typography>
           
                    <Box component="form" onSubmit={handlePasswordResetRequest}>
                        <Stack spacing={3}>
                            <TextField
                              margin="normal"
                              required
                              fullWidth
                              label="User Name"
                              value={formData.username}
                              onChange={handleInputChange('username')}
                              error={!!errors.username}
                              helperText={errors.username}
                              sx={ textFieldStyles }
                              disabled={isLoading}
                            />

                            <TextField
                              margin="normal"
                              required
                              fullWidth
                              label="Email Address"
                              value={formData.emailaddr}
                              onChange={handleInputChange('emailaddr')}
                              error={!!errors.emailaddr}
                              helperText={errors.emailaddr}
                              sx={ textFieldStyles }
                              disabled={isLoading}
                            />
                            
                            <ButtonGroup 
                              size="small"
                              variant="contained" 
                              aria-label="Basic button group" 
                              fullWidth
                              
                              sx={{ pt: 3 }}
                            >
                                <Button
                                  onClick={handleClose}
                                  type="button"
                                  sx={ Styles.buttonStyle }
                                  color="error"
                                  disabled={isLoading}
                                >
                                  Cancel
                                </Button>              
                                <Button
                                  type="submit"
                                  disabled={isFormValid || isLoading}
                                  sx={{ ...Styles.buttonStyle, ml: 4 }}
                                >
                                  {isLoading ? 'Sending...' : 'Send Reset Email'}
                                </Button>
                              </ButtonGroup>
                            </Stack>
                        </Box>
                    </Box>
            </Box>        
      </Modal>
    </>
  );
}